import { Avatar, Flex, Input, Typography } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { generateNameSpaceColor } from "../../utils/color";
import { phoneNumberFormatE164, phoneNumberValidate } from "../../utils/phoneLib";
import { useState, useMemo } from "react";
import { FixedSizeList as List } from 'react-window';
import { Contact } from "@data-phone/react-generic/dist/components/Forms/Contacts";
import { formatContactName } from "../../utils/contacts";

const { Text } = Typography;

const avatarStyle: React.CSSProperties = {
  color: "black",
  marginRight: "8px",
};
const conversationMessageStyle: React.CSSProperties = {
  fontSize: "11px",
  fontWeight: "500",
  lineHeight: "14px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "rgb(119, 117, 135)",
};

const AllContactTextStyle: React.CSSProperties = {
  fontSize:"14px",
  lineHeight:"20px",
  color:"gray",
  padding:"5px"
}

export const ContactList = ({
  handleSubmit,
  onError,
  searchInput: numberInput,
  selectedPhones,
  isModal,
  handleUpdateContact
}: {
  handleSubmit: (value: string) => void;
  onError: (message: string) => void;
  searchInput: string;
  selectedPhones?: string[];
  isModal?: boolean
  handleUpdateContact?:(value:Contact) => void
}) => {
  const { data } = useSelector((state: RootState) => state.contacts);
  const [searchInput, setSearchInput] = useState<string>("");
  const selectedPhoneSet = useMemo(() => new Set(selectedPhones ?? []), [selectedPhones]);

  const processedData = useMemo(() => {
    const input  = numberInput.toLowerCase();

    return data
      .flatMap((contact) => {
          if (!contact.phones || contact.phones.length === 0) {
            return [];
          }
          
          return contact.phones.map((phone) => ({
            ...contact,
            phone: phone.phone_number,
            _cached_name: formatContactName(contact).toLowerCase(),
          }))
      })
      .filter((item) => {
        if  (!item.phone) return false;

        return item._cached_name.includes(input) ||
               item.phone.includes(input);
      })
      .sort((a, b) => {
        const aName = a._cached_name;
        const bName = b._cached_name;
        
        if (aName.includes(input) || a.phone.includes(input)) {
          return -1;
        } else if (bName.includes(input) || b.phone.includes(input)) {
          return 1;
        }
        return aName.localeCompare(bName);
      });
  }, [data, numberInput]);

  const handleClick = (value: string) => {
    if (!phoneNumberValidate(value)) {
      onError("Invalid phone number");
      return;
    }
    handleSubmit(value);
  };

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const contact = processedData[index];
    const color = generateNameSpaceColor(contact.first_name);
    const normalizedPhone = phoneNumberFormatE164(contact.phone);
    const isSelected = Boolean(normalizedPhone && selectedPhoneSet.has(normalizedPhone));
    
    return (
      <div style={{ ...style, overflow: "hidden" }}>
        <Flex
          onClick={() => {
            if (isSelected) return;
            handleClick(contact.phone);
            if (handleUpdateContact) {
              handleUpdateContact(contact);
            }
          }}
          style={{
            background: isSelected ? "#e9d5ff" : "#ffffff",
            border: isSelected ? "2px solid #653BFA" : "1px solid #e5e7eb",
            borderRadius: "8px",
            boxSizing: "border-box",
            cursor: isSelected ? "default" : "pointer",
            height: "100%",
            padding: "6px 8px",
            width: "100%",
          }}
        >
          <Avatar
            style={{ ...avatarStyle, background: color }}
            size={32}
          >
            {contact.first_name?.[0] || '?'}
          </Avatar>
          <Flex vertical={true}>
            <Text style={{ fontSize: "12px", fontWeight: 500, lineHeight: "16px" }}>
              {`${contact.first_name || ""} ${contact.middle_name || ""} ${contact.last_name || ""}`}
            </Text>
            <Text style={conversationMessageStyle}>
              {contact.phone}
            </Text>
          </Flex>
          {isSelected && <Text style={{ color: "#5b21b6", fontSize: "10px", fontWeight: 600, marginLeft: "auto" }}>Selected</Text>}
        </Flex>
      </div>
    );
  };

  return (
    <>
      {data.length > 0 ? (
        <div
          style={{
            background: "#F5F6F7",
            padding: "10px",
            height: "max-content",
            border: "1px solid #DFE2E8",
            borderRadius: "10px",
            position: isModal ? "initial" : "absolute",
            transform: isModal ? "none" : "translate(-50%, -50%)",
            left: "50%",
            top: "28%",
          }}
        >
          <Input
            style={{
              border: "1px solid #777587",
              borderRadius: "25px",
              background: "#F5F6F7",
            }}
            size="middle"
            onChange={(e) => setSearchInput(e.target.value)}
            value={searchInput}
            placeholder="Search "
            prefix={"To:"}
          />
          <Text style={AllContactTextStyle}>All Contacts</Text>
          
          <div style={{ height: "200px", width: "100%" }}>
            <List
              height={200}
              itemCount={processedData.length}
              itemSize={60}
              width="100%"
            >
              {Row}
            </List>
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
